import React from 'react';
import {
  ArrowForwardRounded,
  BoltRounded,
  LocationOnRounded,
  MyLocationRounded,
  NearMeRounded,
  ShieldRounded,
  TwoWheelerRounded,
  VerifiedRounded
} from '@mui/icons-material';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  calcEst,
  FONT_FAMILY,
  optionsRequired,
  showEst
} from '../common/sharedFunctions';

const accent = '#B7EE00';
const ink = '#111411';

const TrustItem = ({ icon, label }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
    <Typography sx={{
      color: 'rgba(255,255,255,0.82)',
      fontFamily: FONT_FAMILY,
      fontSize: { xs: '0.75rem', sm: '0.82rem' },
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}>
      {label}
    </Typography>
  </Stack>
);

const LocationRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box sx={{
      alignItems: 'center',
      backgroundColor: '#F1F4EC',
      borderRadius: '12px',
      color: ink,
      display: 'flex',
      height: 42,
      justifyContent: 'center',
      minWidth: 42
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{
        color: '#788078',
        fontFamily: FONT_FAMILY,
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      }}>
        {label}
      </Typography>
      <Typography noWrap sx={{
        color: ink,
        fontFamily: FONT_FAMILY,
        fontSize: { xs: '0.9rem', sm: '1rem' },
        fontWeight: 700
      }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const RidePreview = ({ t }) => (
  <Box sx={{
    margin: '0 auto',
    maxWidth: 500,
    position: 'relative',
    width: '100%'
  }}>
    <Box sx={{
      background: 'linear-gradient(145deg, rgba(183,238,0,0.32), rgba(183,238,0,0.02))',
      borderRadius: '44px',
      bottom: -18,
      filter: 'blur(2px)',
      left: -18,
      position: 'absolute',
      right: 18,
      top: 18
    }} />

    <Box sx={{
      backgroundColor: '#FFFFFF',
      border: '1px solid rgba(255,255,255,0.28)',
      borderRadius: { xs: '24px', sm: '32px' },
      boxShadow: '0 30px 80px rgba(0,0,0,0.38)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            backgroundColor: accent,
            borderRadius: '50%',
            boxShadow: '0 0 0 5px rgba(183,238,0,0.18)',
            height: 9,
            width: 9
          }} />
          <Typography sx={{
            color: ink,
            fontFamily: FONT_FAMILY,
            fontSize: '0.76rem',
            fontWeight: 800,
            letterSpacing: '0.08em'
          }}>
            LAIWANI LIVE
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <VerifiedRounded sx={{ color: '#219653', fontSize: 18 }} />
          <Typography sx={{
            color: '#596159',
            fontFamily: FONT_FAMILY,
            fontSize: '0.72rem',
            fontWeight: 700
          }}>
            {t('verified', { defaultValue: 'Verified' })}
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{
        backgroundColor: '#E9EEE4',
        backgroundImage: `linear-gradient(rgba(17,20,17,0.055) 1px, transparent 1px),
          linear-gradient(90deg, rgba(17,20,17,0.055) 1px, transparent 1px)`,
        backgroundSize: '34px 34px',
        height: { xs: 205, sm: 245 },
        overflow: 'hidden',
        position: 'relative'
      }}>
        <Box sx={{
          border: '4px solid rgba(183,238,0,0.85)',
          borderBottomColor: 'transparent',
          borderLeftColor: 'transparent',
          borderRadius: '50%',
          height: 245,
          left: '12%',
          position: 'absolute',
          top: '28%',
          transform: 'rotate(-16deg)',
          width: '78%'
        }} />
        <Box sx={{
          backgroundColor: ink,
          border: '5px solid #FFFFFF',
          borderRadius: '50%',
          boxShadow: '0 10px 26px rgba(0,0,0,0.24)',
          color: accent,
          display: 'grid',
          height: 58,
          placeItems: 'center',
          position: 'absolute',
          right: '17%',
          top: '24%',
          width: 58
        }}>
          <TwoWheelerRounded sx={{ fontSize: 29 }} />
        </Box>
        <Box sx={{
          backgroundColor: accent,
          border: '5px solid #FFFFFF',
          borderRadius: '50%',
          bottom: '20%',
          boxShadow: '0 10px 26px rgba(0,0,0,0.2)',
          height: 26,
          left: '18%',
          position: 'absolute',
          width: 26
        }} />
        <Box sx={{
          backgroundColor: 'rgba(255,255,255,0.94)',
          border: '1px solid rgba(17,20,17,0.08)',
          borderRadius: '14px',
          bottom: 14,
          boxShadow: '0 10px 30px rgba(17,20,17,0.12)',
          left: 14,
          px: 1.5,
          py: 1,
          position: 'absolute'
        }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <NearMeRounded sx={{ color: '#219653', fontSize: 17 }} />
            <Typography sx={{
              color: ink,
              fontFamily: FONT_FAMILY,
              fontSize: '0.74rem',
              fontWeight: 800
            }}>
              {t('riders_nearby', { defaultValue: 'Riders nearby' })}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={1.75}>
          <LocationRow
            icon={<MyLocationRounded sx={{ color: '#219653', fontSize: 21 }} />}
            label={t('pickup_location')}
            value={t('your_current_location', { defaultValue: 'Your current location' })}
          />
          <Box sx={{ borderTop: '1px solid #E8ECE5', ml: '54px' }} />
          <LocationRow
            icon={<LocationOnRounded sx={{ color: '#E64C3C', fontSize: 22 }} />}
            label={t('drop_location')}
            value={t('where_are_you_going', { defaultValue: 'Where are you going?' })}
          />
        </Stack>
      </Box>
    </Box>

    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        backgroundColor: accent,
        border: '4px solid #111827',
        borderRadius: '16px',
        bottom: { xs: -18, sm: 24 },
        boxShadow: '0 16px 34px rgba(0,0,0,0.28)',
        color: ink,
        px: 1.75,
        py: 1.2,
        position: 'absolute',
        right: { xs: 14, sm: -22 },
        zIndex: 3
      }}
    >
      <BoltRounded sx={{ fontSize: 20 }} />
      <Typography sx={{
        fontFamily: FONT_FAMILY,
        fontSize: '0.75rem',
        fontWeight: 900
      }}>
        {t('quick_booking', { defaultValue: 'Quick booking' })}
      </Typography>
    </Stack>
  </Box>
);

const Hero = ({ role }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const isRTL = i18n.dir();
  const loggedIn = Boolean(auth.profile && auth.profile.uid);

  const bookingDestination =
    (role === 'driver') ||
    (calcEst && !showEst && !optionsRequired && (role === 'admin' || role === 'fleetadmin'))
      ? '/bookings'
      : '/addbookings';

  const handleBooking = () => {
    navigate(loggedIn ? bookingDestination : '/login');
  };

  return (
    <Box
      component="section"
      sx={{
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0A0E0C 0%, #121B2C 52%, #0D1322 100%)',
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        display: 'flex',
        minHeight: { xs: 'auto', md: '88vh' },
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Box sx={{
        background: 'radial-gradient(circle, rgba(183,238,0,0.18) 0%, rgba(183,238,0,0) 68%)',
        height: 720,
        left: '-22%',
        pointerEvents: 'none',
        position: 'absolute',
        top: '-34%',
        width: 720
      }} />
      <Box sx={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        bottom: 0,
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
        opacity: 0.2,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        top: 0,
        width: '48%'
      }} />

      <Container
        maxWidth="lg"
        sx={{
          pb: { xs: 8, md: 10 },
          pt: { xs: 15, sm: 17, md: 18 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Box sx={{
          alignItems: 'center',
          display: 'grid',
          gap: { xs: 7, md: 7, lg: 10 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1.05fr 0.95fr' }
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ backgroundColor: accent, borderRadius: '20px', height: 8, width: 28 }} />
              <Typography sx={{
                color: 'rgba(255,255,255,0.72)',
                fontFamily: FONT_FAMILY,
                fontSize: { xs: '0.76rem', sm: '0.84rem' },
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase'
              }}>
                {t('shillong_local_ride_network', { defaultValue: "Shillong's local ride network" })}
              </Typography>
            </Stack>

            <Typography
              component="h1"
              sx={{
                color: '#FFFFFF',
                fontFamily: FONT_FAMILY,
                fontSize: { xs: '2.55rem', sm: '4rem', md: '4.25rem', lg: '5rem' },
                fontWeight: 800,
                letterSpacing: '-0.045em',
                lineHeight: 0.98,
                mb: 3,
                maxWidth: 700
              }}
            >
              {t('book_your_title')}.{' '}
              <Box component="span" sx={{ color: accent }}>
                {t('your_way', { defaultValue: 'Your way.' })}
              </Box>
            </Typography>

            <Typography sx={{
              color: 'rgba(255,255,255,0.74)',
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              lineHeight: 1.7,
              maxWidth: 610,
              mb: 4
            }}>
              {t('about_us_content2')}
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              useFlexGap
              sx={{ alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 4 }}
            >
              <Button
                endIcon={<ArrowForwardRounded />}
                onClick={handleBooking}
                size="large"
                variant="contained"
                sx={{
                  backgroundColor: accent,
                  borderRadius: '14px',
                  boxShadow: '0 14px 30px rgba(183,238,0,0.18)',
                  color: ink,
                  fontFamily: FONT_FAMILY,
                  fontSize: '1rem',
                  fontWeight: 900,
                  minHeight: 54,
                  px: 3,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#CFFF1D',
                    boxShadow: '0 18px 36px rgba(183,238,0,0.25)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {role === 'driver' || bookingDestination === '/bookings'
                  ? t('info')
                  : t('book_your_ride_menu')}
              </Button>
              <Button
                component="a"
                href="#how-it-works"
                size="large"
                sx={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '14px',
                  color: '#FFFFFF',
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  minHeight: 54,
                  px: 3,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
                }}
              >
                {t('see_how_it_works', { defaultValue: 'See how it works' })}
              </Button>
            </Stack>

            <Stack
              direction="row"
              useFlexGap
              sx={{ flexWrap: 'wrap', gap: { xs: 1.5, sm: 2.5 } }}
            >
              <TrustItem
                icon={<VerifiedRounded sx={{ fontSize: 18 }} />}
                label={t('verified_riders', { defaultValue: 'Verified riders' })}
              />
              <TrustItem
                icon={<TwoWheelerRounded sx={{ fontSize: 18 }} />}
                label={t('built_for_shillong', { defaultValue: 'Built for Shillong' })}
              />
              <TrustItem
                icon={<ShieldRounded sx={{ fontSize: 18 }} />}
                label={t('safer_every_trip', { defaultValue: 'Safety every trip' })}
              />
            </Stack>
          </Box>

          <RidePreview t={t} />
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
